import React, { useState, useRef, useEffect } from 'react';
import { Search, X } from 'lucide-react';
import type { ExerciseDefinition, MovementPattern } from '../types';
import { MOVEMENT_PATTERN_LABELS, MUSCLE_GROUP_LABELS, EXERCISE_TAG_LABELS } from '../types';
import { searchExercises, getAllExercises } from '../data/exercises';
import { useCustomExercisesStore } from '../store/useCustomExercisesStore';

interface Props {
  onSelect: (def: ExerciseDefinition) => void;
  onClose: () => void;
  warmupOnly?: boolean;
}

export default function ExerciseSearch({ onSelect, onClose, warmupOnly }: Props) {
  const [query, setQuery] = useState('');
  const [filterPattern, setFilterPattern] = useState<MovementPattern | ''>('');
  const [highlightIdx, setHighlightIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const customExercises = useCustomExercisesStore((s) => s.customExercises);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  let results = query ? searchExercises(query, customExercises) : getAllExercises(customExercises);
  if (warmupOnly) {
    results = results.filter((e) => e.tag === 'warmup');
  }
  if (filterPattern) {
    results = results.filter((e) => e.movementPattern === filterPattern);
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && results[highlightIdx]) {
      e.preventDefault();
      onSelect(results[highlightIdx]);
      onClose();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  // Scroll highlighted item into view
  useEffect(() => {
    const el = listRef.current?.children[highlightIdx] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlightIdx]);

  const tagColor = (tag: string) => {
    switch (tag) {
      case 'compound':
        return 'bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300';
      case 'isolation':
        return 'bg-purple-100 dark:bg-purple-950 text-purple-700 dark:text-purple-300';
      case 'warmup':
        return 'bg-orange-100 dark:bg-orange-950 text-orange-700 dark:text-orange-300';
      default:
        return 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300';
    }
  };

  return (
    <div className="animate-slide-down" onKeyDown={handleKeyDown}>
      {/* Search input */}
      <div className="relative mb-2">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setHighlightIdx(0);
          }}
          placeholder="Szukaj ćwiczeń... (wpisz nazwę, wzorzec lub mięsień)"
          className="w-full pl-8 pr-8 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
        />
        <button
          onClick={onClose}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
        >
          <X size={14} />
        </button>
      </div>

      {/* Pattern filter chips */}
      <div className="flex gap-1 flex-wrap mb-2">
        <button
          onClick={() => setFilterPattern('')}
          className={`px-2 py-0.5 rounded text-[10px] font-semibold transition ${
            filterPattern === ''
              ? 'bg-brand-100 dark:bg-brand-950 text-brand-700 dark:text-brand-300'
              : 'bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700'
          }`}
        >
          Wszystkie
        </button>
        {(Object.keys(MOVEMENT_PATTERN_LABELS) as MovementPattern[]).map((p) => (
          <button
            key={p}
            onClick={() => {
              setFilterPattern(filterPattern === p ? '' : p);
              setHighlightIdx(0);
            }}
            className={`px-2 py-0.5 rounded text-[10px] font-semibold transition ${
              filterPattern === p
                ? 'bg-brand-100 dark:bg-brand-950 text-brand-700 dark:text-brand-300'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700'
            }`}
          >
            {MOVEMENT_PATTERN_LABELS[p]}
          </button>
        ))}
      </div>

      {/* Results list */}
      <div
        ref={listRef}
        className="max-h-[240px] overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 divide-y divide-slate-100 dark:divide-slate-700"
      >
        {results.length === 0 && (
          <div className="p-4 text-center text-xs text-slate-400">Nie znaleziono ćwiczeń</div>
        )}
        {results.map((ex, idx) => (
          <button
            key={ex.id}
            onClick={() => {
              onSelect(ex);
              onClose();
            }}
            onMouseEnter={() => setHighlightIdx(idx)}
            className={`w-full text-left px-3 py-2 text-sm transition ${
              idx === highlightIdx
                ? 'bg-brand-50 dark:bg-brand-950'
                : 'hover:bg-slate-50 dark:hover:bg-slate-750'
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="font-medium">{ex.name}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${tagColor(ex.tag)}`}>
                {EXERCISE_TAG_LABELS[ex.tag] || ex.tag}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-[10px] text-slate-400">
                {MOVEMENT_PATTERN_LABELS[ex.movementPattern]}
              </span>
              <span className="text-[10px] text-slate-300 dark:text-slate-600">•</span>
              <span className="text-[10px] text-slate-400">
                {MUSCLE_GROUP_LABELS[ex.primaryMuscle]}
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
